import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { supabase } from '../../lib/supabase';
import SearchBox from './components/SearchBox';
import { useSEO } from '@/hooks/useSEO';

interface PricingItem {
  id: string;
  brand: string;
  model: string;
  series: string | null;
  repairType: string;
  price: number;
  estimatedTime: string | null;
  inStock: boolean;
}

const BRANDS = ['全部', 'iPhone', 'iPad', 'MacBook', 'Samsung', 'Asus', 'Google'];

const IPHONE_ORDER: string[] = [
  'iPhone 17 Pro Max', 'iPhone 17 Pro', 'iPhone 17', 'iPhone Air',
  'iPhone 16 Pro Max', 'iPhone 16 Pro', 'iPhone 16 Plus', 'iPhone 16', 'iPhone 16e',
  'iPhone 15 Pro Max', 'iPhone 15 Pro', 'iPhone 15 Plus', 'iPhone 15',
  'iPhone 14 Pro Max', 'iPhone 14 Pro', 'iPhone 14 Plus', 'iPhone 14',
  'iPhone 13 Pro Max', 'iPhone 13 Pro', 'iPhone 13', 'iPhone 13 mini',
  'iPhone 12 Pro Max', 'iPhone 12 Pro', 'iPhone 12', 'iPhone 12 mini',
  'iPhone 11 Pro Max', 'iPhone 11 Pro', 'iPhone 11',
  'iPhone Xs Max', 'iPhone Xs', 'iPhone X', 'iPhone XR',
  'iPhone SE3', 'iPhone SE2', 'iPhone SE',
  'iPhone 8 Plus', 'iPhone 8',
  'iPhone 7 Plus', 'iPhone 7',
  'iPhone 6S Plus', 'iPhone 6S', 'iPhone 6 Plus', 'iPhone 6',
  'iPhone 5S', 'iPhone 5',
];

// iPhone 系列分組定義
const IPHONE_SERIES_ORDER: string[] = [
  'iPhone 17 系列',
  'iPhone 16 系列',
  'iPhone 15 系列',
  'iPhone 14 系列',
  'iPhone 13 系列',
  'iPhone 12 系列',
  'iPhone 11 系列',
  'iPhone X 系列',
  'iPhone SE 系列',
  'iPhone 8 系列',
  'iPhone 7 系列',
  'iPhone 6 系列',
  'iPhone 5 系列',
];

const IPHONE_SERIES_MAP: Record<string, string> = {
  'iPhone 17 Pro Max': 'iPhone 17 系列',
  'iPhone 17 Pro': 'iPhone 17 系列',
  'iPhone 17': 'iPhone 17 系列',
  'iPhone Air': 'iPhone 17 系列',
  'iPhone 16 Pro Max': 'iPhone 16 系列',
  'iPhone 16 Pro': 'iPhone 16 系列',
  'iPhone 16 Plus': 'iPhone 16 系列',
  'iPhone 16': 'iPhone 16 系列',
  'iPhone 16e': 'iPhone 16 系列',
  'iPhone 15 Pro Max': 'iPhone 15 系列',
  'iPhone 15 Pro': 'iPhone 15 系列',
  'iPhone 15 Plus': 'iPhone 15 系列',
  'iPhone 15': 'iPhone 15 系列',
  'iPhone 14 Pro Max': 'iPhone 14 系列',
  'iPhone 14 Pro': 'iPhone 14 系列',
  'iPhone 14 Plus': 'iPhone 14 系列',
  'iPhone 14': 'iPhone 14 系列',
  'iPhone 13 Pro Max': 'iPhone 13 系列',
  'iPhone 13 Pro': 'iPhone 13 系列',
  'iPhone 13': 'iPhone 13 系列',
  'iPhone 13 mini': 'iPhone 13 系列',
  'iPhone 12 Pro Max': 'iPhone 12 系列',
  'iPhone 12 Pro': 'iPhone 12 系列',
  'iPhone 12': 'iPhone 12 系列',
  'iPhone 12 mini': 'iPhone 12 系列',
  'iPhone 11 Pro Max': 'iPhone 11 系列',
  'iPhone 11 Pro': 'iPhone 11 系列',
  'iPhone 11': 'iPhone 11 系列',
  'iPhone Xs Max': 'iPhone X 系列',
  'iPhone Xs': 'iPhone X 系列',
  'iPhone X': 'iPhone X 系列',
  'iPhone XR': 'iPhone X 系列',
  'iPhone SE3': 'iPhone SE 系列',
  'iPhone SE2': 'iPhone SE 系列',
  'iPhone SE': 'iPhone SE 系列',
  'iPhone 8 Plus': 'iPhone 8 系列',
  'iPhone 8': 'iPhone 8 系列',
  'iPhone 7 Plus': 'iPhone 7 系列',
  'iPhone 7': 'iPhone 7 系列',
  'iPhone 6S Plus': 'iPhone 6 系列',
  'iPhone 6S': 'iPhone 6 系列',
  'iPhone 6 Plus': 'iPhone 6 系列',
  'iPhone 6': 'iPhone 6 系列',
  'iPhone 5S': 'iPhone 5 系列',
  'iPhone 5': 'iPhone 5 系列',
};

// MacBook 系列排序
const MACBOOK_SERIES_ORDER: string[] = [
  'MacBook Air 13 系列',
  'MacBook Pro 13 系列',
  'MacBook Pro 14 系列',
  'MacBook Air 15 系列',
  'MacBook Pro 15/16 系列',
];

// Samsung 系列排序
const SAMSUNG_SERIES_ORDER: string[] = [
  'Samsung Z 系列',
  'Samsung Note 系列',
  'Samsung S 系列',
  'Samsung A 系列',
];

// Samsung Z 系列型號排序
const SAMSUNG_Z_ORDER: string[] = [
  'Z Fold7 (SM-F966)', 'Z Fold6 (SM-F956)', 'Z Fold5 (SM-F946)',
  'Z Fold4 (SM-F936)', 'Z Fold3 (SM-F926)', 'Z Fold2 (SM-F916)', 'Z Fold (SM-F900)',
  'Z Flip7 (SM-F751)', 'Z Flip6 (SM-F741)', 'Z Flip5 (SM-F731)',
  'Z Flip4 (SM-F721)', 'Z Flip3 (SM-F711)',
];

// Samsung Note 系列型號排序
const SAMSUNG_NOTE_ORDER: string[] = [
  'Note20 Ultra (N9860)', 'Note20 (N9810)',
  'Note10 Lite (N770)', 'Note10+ (N9750)', 'Note10 (N9700)',
  'Note9 (N960)', 'Note8 (N950)',
];

// Samsung S 系列型號排序
const SAMSUNG_S_ORDER: string[] = [
  'S25 Ultra (S9380)', 'S25+ (S9360)', 'S25 FE (S731B)', 'S25 (S9310)',
  'S24 (S9210)', 'S24+ (S9260)', 'S24 Ultra (S9280)',
  'S23 (S9110)', 'S23+ (S9160)', 'S23 Ultra (S9180)', 'S23 FE (S711)',
  'S22+ (S9060)', 'S22 Ultra (S9080)', 'S22 (S9010)',
  'S21 (G991)', 'S21+ (G996)', 'S21 Ultra (G998)', 'S21 FE (G990)',
  'S20 Ultra (G988)', 'S20+ (G9860)', 'S20 FE (G871)', 'S20 (G9810)',
  'S10+ (G975)', 'S10 (G973)',
];

// Samsung A 系列型號排序
const SAMSUNG_A_ORDER: string[] = [
  'A13 5G (A136B)', 'A14 5G (A146P)', 'A16 5G (A1660)', 'A15 5G (A1560)',
  'A21S (A217F)', 'A22 5G (A226)', 'A23 5G (A236E)',
  'A33 5G (A336)', 'A34 5G (A346)', 'A35 5G (A3560)',
  'A42 5G (A426)',
  'A50 (A505)', 'A51 (A515)', 'A51 5G (A516)', 'A52 5G (A526)', 'A52S 5G (A528)', 'A53 5G (A536)', 'A54 5G (A546)', 'A55 5G (A5560)',
  'A70 (A7050)', 'A71 (A715)', 'A71 5G (A716)',
  'M14 5G (M146B)', 'M32', 'M33', 'M34 5G / A25 5G', 'M53 5G (M536)',
];

// ASUS 系列排序
const ASUS_SERIES_ORDER: string[] = [
  'ROG 系列',
  'Zenfone 系列',
];

// ASUS ROG 系列型號排序
const ASUS_ROG_ORDER: string[] = [
  'ROG Phone 9 PRO',
  'ROG Phone 9',
  'ROG Phone 8 PRO',
  'ROG Phone 8',
  'ROG Phone 7 (AI2205)',
  'ROG Phone 6D Ultimate',
  'ROG Phone 6 (AI2201)',
  'ROG Phone 5S (ZS676KS)',
  'ROG Phone 5 (ZS673KS)',
  'ROG Phone 3 (ZS661KL)',
  'ROG Phone 2 (ZS660KL)',
  'ROG Phone 1 (ZS600KL)',
];

// ASUS Zenfone 系列型號排序
const ASUS_ZENFONE_ORDER: string[] = [
  'Zenfone 11 Ultra',
  'Zenfone 10',
  'Zenfone 9',
  'Zenfone 8 (ZS590KS)',
  'Zenfone 8 Flip (ZS672KS)',
  'Zenfone 7 Pro (ZS671KS)',
  'Zenfone 7 (ZS670KS)',
  'Zenfone 6 (ZS630KL)',
];

// Google Pixel 型號排序
const GOOGLE_PIXEL_ORDER: string[] = [
  'Pixel 10 Pro XL', 'Pixel 10 Pro', 'Pixel 10',
  'Pixel 9 Pro XL', 'Pixel 9 Pro', 'Pixel 9A', 'Pixel 9',
  'Pixel 8 Pro', 'Pixel 8A', 'Pixel 8',
  'Pixel 7 Pro', 'Pixel 7A', 'Pixel 7',
  'Pixel 6 Pro', 'Pixel 6A', 'Pixel 6',
  'Pixel 5A', 'Pixel 5',
  'Pixel 4 XL', 'Pixel 4A (5G)', 'Pixel 4A (4G)', 'Pixel 4',
  'Pixel 3 XL', 'Pixel 3A XL', 'Pixel 3A', 'Pixel 3',
];

function getAsusModelSortKey(model: string, series: string): number {
  if (series === 'ROG 系列') {
    const idx = ASUS_ROG_ORDER.indexOf(model);
    return idx === -1 ? 999 : idx;
  }
  if (series === 'Zenfone 系列') {
    const idx = ASUS_ZENFONE_ORDER.indexOf(model);
    return idx === -1 ? 999 : idx;
  }
  return 999;
}

function getSamsungModelSortKey(model: string, series: string): number {
  if (series === 'Samsung Z 系列') {
    const idx = SAMSUNG_Z_ORDER.indexOf(model);
    return idx === -1 ? 999 : idx;
  }
  if (series === 'Samsung Note 系列') {
    const idx = SAMSUNG_NOTE_ORDER.indexOf(model);
    return idx === -1 ? 999 : idx;
  }
  if (series === 'Samsung S 系列') {
    const idx = SAMSUNG_S_ORDER.indexOf(model);
    return idx === -1 ? 999 : idx;
  }
  if (series === 'Samsung A 系列') {
    const idx = SAMSUNG_A_ORDER.indexOf(model);
    return idx === -1 ? 999 : idx;
  }
  return 999;
}

// MacBook 排序 — 用型號編號（A-number）作為唯一識別鍵
const MACBOOK_ORDER_BY_NUMBER: Array<{ key: string; idx: number }> = [
  // MacBook Air 13 系列
  { key: 'A1932', idx: 0 },
  { key: 'A2179', idx: 1 },
  { key: 'A2337', idx: 2 },
  { key: 'A2681', idx: 3 },
  { key: 'A3113', idx: 4 },
  { key: 'A3240', idx: 5 },
  // MacBook Pro 13 系列
  { key: 'A1708', idx: 10 },
  { key: 'A1706', idx: 11 },
  { key: 'A1989', idx: 12 },
  { key: 'A2159', idx: 13 },
  { key: 'A2251', idx: 14 },
  { key: 'A2289', idx: 15 },
  // A2338 出現兩次（M1 和 M2），用年份區分
  { key: 'A2338', idx: 16 },
  // MacBook Pro 14 系列
  { key: 'A2442', idx: 20 },
  { key: 'A2779', idx: 21 },
  { key: 'A2918', idx: 22 },
  { key: 'A2992', idx: 23 },
  { key: 'A3112', idx: 24 },
  { key: 'A3401', idx: 25 },
  { key: 'A3185', idx: 26 },
  // MacBook Air 15 系列
  { key: 'A2941', idx: 30 },
  { key: 'A3114', idx: 31 },
  { key: 'A3241', idx: 32 },
  // MacBook Pro 15/16 系列
  { key: 'A1707', idx: 40 },
  { key: 'A1990', idx: 41 },
  { key: 'A2141', idx: 42 },
  { key: 'A2485', idx: 43 },
  { key: 'A2780', idx: 44 },
  { key: 'A2991', idx: 45 },
  { key: 'A3403', idx: 46 },
  { key: 'A3186', idx: 47 },
];

// iPad 系列分組定義
const IPAD_SERIES_ORDER: string[] = [
  'iPad 系列',
  'iPad mini 系列',
  'iPad Air 系列',
  'iPad Pro 系列',
];

const IPAD_SERIES_MAP: Record<string, string> = {
  'iPad (第 5 代)  A1822 / A1823': 'iPad 系列',
  'iPad (第 6 代)  A1893 / A1954': 'iPad 系列',
  'iPad (第 7 代)  A2197 / A2198 / A2200': 'iPad 系列',
  'iPad (第 8 代)  A2270 / A2428 / A2429 / A2430': 'iPad 系列',
  'iPad (第 9 代)  A2602 / A2603 / A2604': 'iPad 系列',
  'iPad (第 10 代)  A2696 / A2757': 'iPad 系列',
  'iPad (A16)  A3354 / A3355': 'iPad 系列',
  'iPad mini (第 4 代)  A1538 / A1550': 'iPad mini 系列',
  'iPad mini (第 5 代)  A2133 / A2124 / A2126': 'iPad mini 系列',
  'iPad mini (第 6 代)  A2567 / A2568': 'iPad mini 系列',
  'iPad mini (A17 Pro)  A2993 / A2995': 'iPad mini 系列',
  'iPad Air 2  A1566 / A1567': 'iPad Air 系列',
  'iPad Air 3  A2152 / A2123 / A2153': 'iPad Air 系列',
  'iPad Air 4  A2316 / A2072 / A2324 / A2325': 'iPad Air 系列',
  'iPad Air 5  A2588 / A2589 / A2591': 'iPad Air 系列',
  'iPad Air (11 吋, M2)  A2902 / A2903': 'iPad Air 系列',
  'iPad Air (13 吋, M2)  A2898 / A2899': 'iPad Air 系列',
  'iPad Air (11 吋, M3)  A3266 / A3267': 'iPad Air 系列',
  'iPad Air (13 吋, M3)  A3268 / A3269': 'iPad Air 系列',
  'iPad Pro (9.7 吋)  A1673 / A1674 / A1675': 'iPad Pro 系列',
  'iPad Pro (10.5 吋)  A1701 / A1709': 'iPad Pro 系列',
  'iPad Pro (11 吋)  A1980 / A2013 / A1934': 'iPad Pro 系列',
  'iPad Pro (11 吋，第 2 代)  A2228 / A2068 / A2230': 'iPad Pro 系列',
  'iPad Pro (11 吋，第 3 代)  A2377 / A2301 / A2459': 'iPad Pro 系列',
  'iPad Pro (11 吋，第 4 代)  A2759 / A2761 / A2435': 'iPad Pro 系列',
  'iPad Pro (11 吋，M4)  A2836 / A2837': 'iPad Pro 系列',
  'iPad Pro (11 吋，M5)  A3357 / A3358': 'iPad Pro 系列',
  'iPad Pro (12.9 吋，第 3 代)  A1876 / A1895 / A2014': 'iPad Pro 系列',
  'iPad Pro (12.9 吋，第 4 代)  A2229 / A2069 / A2232': 'iPad Pro 系列',
  'iPad Pro (12.9 吋，第 5 代)  A2378 / A2461 / A2379': 'iPad Pro 系列',
  'iPad Pro (12.9 吋，第 6 代)  A2436 / A2437 / A2764': 'iPad Pro 系列',
  'iPad Pro (13 吋，M4)  A2925 / A2926': 'iPad Pro 系列',
  'iPad Pro (13 吋，M5)  A3360 / A3361': 'iPad Pro 系列',
};

// iPad 排序 — 用型號編號作為唯一識別鍵，完全避免空格/標點差異
const IPAD_ORDER_BY_NUMBER: Array<{ key: string; idx: number }> = [
  { key: 'A1822', idx: 0 }, { key: 'A1893', idx: 1 }, { key: 'A2197', idx: 2 },
  { key: 'A2270', idx: 3 }, { key: 'A2602', idx: 4 }, { key: 'A2696', idx: 5 },
  { key: 'A3354', idx: 6 },
  { key: 'A1538', idx: 7 }, { key: 'A2133', idx: 8 }, { key: 'A2567', idx: 9 },
  { key: 'A2993', idx: 10 },
  { key: 'A1566', idx: 11 }, { key: 'A2152', idx: 12 }, { key: 'A2316', idx: 13 },
  { key: 'A2588', idx: 14 },
  { key: 'A2902', idx: 15 }, { key: 'A2898', idx: 16 },
  { key: 'A3266', idx: 17 }, { key: 'A3268', idx: 18 },
  { key: 'A1673', idx: 19 }, { key: 'A1701', idx: 20 },
  { key: 'A1980', idx: 21 }, { key: 'A2228', idx: 22 }, { key: 'A2377', idx: 23 },
  { key: 'A2759', idx: 24 }, { key: 'A2836', idx: 25 }, { key: 'A3357', idx: 26 },
  { key: 'A1876', idx: 27 }, { key: 'A2229', idx: 28 }, { key: 'A2378', idx: 29 },
  { key: 'A2436', idx: 30 }, { key: 'A2925', idx: 31 }, { key: 'A3360', idx: 32 },
];

const REPAIR_ORDER: Record<string, number> = {
  '原廠螢幕': 1, '副廠螢幕': 2,
  'iPad 玻璃破裂': 3, 'iPad 液晶螢幕': 4,
  'MacBook 原廠螢幕-總成': 5, 'MacBook 副廠螢幕-總成': 6, 'MacBook 液晶更換': 7, 'MacBook 膠條/LOGO': 8,
  '螢幕破裂': 8, '內螢幕破裂/總成帶框': 9, '內螢幕破裂': 10, '外螢幕破裂': 11, '總成帶框': 12,
  '原廠電池': 20, '副廠電池': 21, '認證電池': 22,
  'MacBook 原廠電池(含鍵盤)': 23, 'MacBook 副廠電池': 24, '電池': 25,
  '原廠 FACE ID': 30, 'FACE ID 維修': 31,
  '前鏡頭更換': 32, '後鏡頭更換': 33, '相機鍵': 34,
  '前/後鏡頭': 35, '後鏡頭/前鏡頭': 36, '後鏡頭': 37, '前鏡頭': 38,
  'HOME鍵': 40, '開機鍵/音量鍵': 41, '背蓋玻璃更換': 42, '背蓋': 43,
  '充電孔（響鈴振動）': 44, '充電/耳機孔/震動': 45,
  '聽筒/麥克風': 46, '聽筒/麥克風/響鈴': 47,
  '天線/感應模組': 48, '排線(wifi/自動關機/顯示異常/鈴聲/轉軸故障)': 49,
  'MacBook 鍵盤': 50, 'MacBook 觸控板': 51, 'MacBook Touch Bar': 52,
  'MacBook 膠條/LOGO': 53, 'MacBook 系統重灌': 54, 'MacBook 泡水清潔': 55,
  '128G 升級': 60, '256G 升級': 61, '512G 升級': 62, '1TB 升級': 63, '2TB 升級': 64,
  'MacBook 512G 升級': 65, 'MacBook 1TB 升級': 66, 'MacBook 2TB 升級': 67,
  'MacBook 16G 記憶體升級': 70, 'MacBook 32G 記憶體升級': 71, 'MacBook 64G 記憶體升級': 72,
  '主機板維修': 90, 'MacBook 主板維修': 91,
};



function sortRepairItems(items: PricingItem[]): PricingItem[] {
  return [...items].sort((a, b) => (REPAIR_ORDER[a.repairType] ?? 80) - (REPAIR_ORDER[b.repairType] ?? 80));
}

function getModelSortKey(brand: string, model: string, series?: string | null): number {
  if (brand === 'iPhone') {
    const idx = IPHONE_ORDER.indexOf(model);
    return idx === -1 ? 999 : idx;
  }
  if (brand === 'iPad') {
    const entry = IPAD_ORDER_BY_NUMBER.find((o) => model.includes(o.key));
    return entry ? entry.idx : 999;
  }
  if (brand === 'MacBook') {
    const entry = MACBOOK_ORDER_BY_NUMBER.find((o) => model.includes(o.key));
    if (entry) {
      if (entry.key === 'A2338') {
        if (model.includes('M2') || model.includes('2022') || model.includes('2021')) return 17;
        return 16;
      }
      return entry.idx;
    }
    return 999;
  }
  if (brand === 'Samsung') {
    return getSamsungModelSortKey(model, series ?? '');
  }
  if (brand === 'Asus') {
    return getAsusModelSortKey(model, series ?? '');
  }
  if (brand === 'Google') {
    const idx = GOOGLE_PIXEL_ORDER.indexOf(model);
    return idx === -1 ? 999 : idx;
  }
  return 0;
}

function showUsedPartWarning(item: PricingItem): boolean {
  if (item.repairType === '相機鍵') return true;
  if (item.repairType === 'FACE ID 維修') {
    if (item.model === 'iPhone Air') return true;
    const m = item.model.match(/iPhone\s+(\d+)/);
    return m ? parseInt(m[1], 10) >= 16 : false;
  }
  if (item.repairType === '前鏡頭更換' || item.repairType === '後鏡頭更換') {
    if (item.model === 'iPhone Air') return true;
    const m = item.model.match(/iPhone\s+(\d+)/);
    return m ? parseInt(m[1], 10) >= 12 : false;
  }
  return false;
}

function showFaceIDOKNotice(item: PricingItem): boolean {
  if (item.repairType !== 'FACE ID 維修') return false;
  if (item.model === 'iPhone Air') return false;
  if (/iPhone\s+X/.test(item.model)) return true;
  const m = item.model.match(/iPhone\s+(\d+)/);
  return m ? parseInt(m[1], 10) >= 11 && parseInt(m[1], 10) <= 15 : false;
}

// 標準化字串：去空格、去特殊符號、轉小寫
function normalizeStr(s: string): string {
  return s.toLowerCase().replace(/[\s\-_(),.|/]/g, '');
}

// 模糊比對（前端過濾用）
function fuzzyMatchLocal(target: string, query: string): boolean {
  if (!query.trim()) return false;
  const t = normalizeStr(target);
  const q = normalizeStr(query);
  if (!q) return false;
  // 直接包含（最優先，支援型號編號如 A2338、2338）
  if (t.includes(q)) return true;
  // 字元順序匹配
  let ti = 0;
  for (let qi = 0; qi < q.length; qi++) {
    const found = t.indexOf(q[qi], ti);
    if (found === -1) return false;
    ti = found + 1;
  }
  return true;
}

export default function PricingPage() {
  useSEO({
    title: '維修報價查詢｜iPhone iPad MacBook Samsung 維修費用',
    description: '即時查詢 iPhone、iPad、MacBook、Samsung、ASUS、Google Pixel 維修費用。輸入型號秒查報價，含螢幕更換、電池維修、主板維修等項目，透明定價不隱藏費用。',
    keywords: 'iPhone維修報價,iPad維修費用,MacBook維修價格,Samsung維修,手機維修費用查詢',
    canonical: '/pricing',
  });

  const [searchParams] = useSearchParams();
  const [allData, setAllData] = useState<PricingItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') ?? '');
  const [selectedBrand, setSelectedBrand] = useState('全部');

  // 按品牌或關鍵字動態查詢
  useEffect(() => {
    const brand = selectedBrand;
    const query = searchQuery.trim();

    if (brand === '全部' && !query) {
      setAllData([]);
      return;
    }

    setLoading(true);

    const ALL_BRANDS = ['iPhone', 'iPad', 'MacBook', 'Samsung', 'Asus', 'Google'];

    const fetchBrand = (b: string) =>
      supabase
        .from('repair_pricing')
        .select('id, brand, model, series, repair_type, price, estimated_time, in_stock')
        .eq('brand', b)
        .limit(10000)
        .then(({ data }) => data ?? []);

    const brandsToFetch = brand !== '全部' ? [brand] : ALL_BRANDS;

    Promise.all(brandsToFetch.map(fetchBrand)).then((results) => {
      const combined = results.flat();
      setAllData(combined.map((d) => ({
        id: d.id,
        brand: d.brand,
        model: d.model,
        series: d.series ?? null,
        repairType: d.repair_type,
        price: d.price,
        estimatedTime: d.estimated_time ?? null,
        inStock: d.in_stock,
      })));
      setLoading(false);
    });
  }, [selectedBrand, searchQuery]);

  // 篩選邏輯：只要有 searchQuery 或選了品牌就顯示
  const hasFilter = searchQuery.trim() !== '' || selectedBrand !== '全部';

  const filteredData = (() => {
    if (!hasFilter) return [];
    let data = allData;
    if (selectedBrand !== '全部') {
      data = data.filter((d) => d.brand === selectedBrand);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim();
      const qNorm = normalizeStr(q);
      data = data.filter((d) => {
        const modelText = d.brand + ' ' + d.model;
        const repairText = d.repairType;
        // 直接包含（不區分大小寫）
        const combined = (modelText + ' ' + repairText).toLowerCase();
        if (combined.includes(q.toLowerCase())) return true;
        // 標準化後包含（支援型號編號如 A2338、2338、a2338）
        const modelNorm = normalizeStr(modelText);
        if (modelNorm.includes(qNorm)) return true;
        // 模糊字元順序匹配
        return fuzzyMatchLocal(modelText, q);
      });
    }
    return data;
  })();

  // 按型號分組
  const grouped: Record<string, PricingItem[]> = {};
  filteredData.forEach((item) => {
    const key = item.brand + '||' + item.model;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(item);
  });

  // 排序分組
  const sortedKeys = Object.keys(grouped).sort((a, b) => {
    const ia = grouped[a][0];
    const ib = grouped[b][0];
    const brandOrder = ['iPhone', 'iPad', 'MacBook', 'Samsung', 'Asus', 'Google'];
    const bd = brandOrder.indexOf(ia.brand) - brandOrder.indexOf(ib.brand);
    if (bd !== 0) return bd;
    // Samsung: 先按系列排，再按型號排
    if (ia.brand === 'Samsung') {
      const sai = SAMSUNG_SERIES_ORDER.indexOf(ia.series ?? '');
      const sbi = SAMSUNG_SERIES_ORDER.indexOf(ib.series ?? '');
      const sd = (sai === -1 ? 999 : sai) - (sbi === -1 ? 999 : sbi);
      if (sd !== 0) return sd;
    }
    // Asus: 先按系列排，再按型號排
    if (ia.brand === 'Asus') {
      const aai = ASUS_SERIES_ORDER.indexOf(ia.series ?? '');
      const abi = ASUS_SERIES_ORDER.indexOf(ib.series ?? '');
      const ad = (aai === -1 ? 999 : aai) - (abi === -1 ? 999 : abi);
      if (ad !== 0) return ad;
    }
    const md = getModelSortKey(ia.brand, ia.model, ia.series) - getModelSortKey(ib.brand, ib.model, ib.series);
    if (md !== 0) return md;
    return ia.model.localeCompare(ib.model);
  });

  // MacBook 按系列再分組
  const macbookSeriesGrouped: Record<string, string[]> = {};
  sortedKeys.forEach((key) => {
    const item = grouped[key][0];
    if (item.brand !== 'MacBook') return;
    const s = item.series ?? '其他';
    if (!macbookSeriesGrouped[s]) macbookSeriesGrouped[s] = [];
    macbookSeriesGrouped[s].push(key);
  });

  const sortedSeriesKeys = Object.keys(macbookSeriesGrouped).sort((a, b) => {
    const ai = MACBOOK_SERIES_ORDER.indexOf(a);
    const bi = MACBOOK_SERIES_ORDER.indexOf(b);
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
  });

  // Samsung 按系列再分組
  const samsungSeriesGrouped: Record<string, string[]> = {};
  sortedKeys.forEach((key) => {
    const item = grouped[key][0];
    if (item.brand !== 'Samsung') return;
    const s = item.series ?? '其他';
    if (!samsungSeriesGrouped[s]) samsungSeriesGrouped[s] = [];
    samsungSeriesGrouped[s].push(key);
  });

  const sortedSamsungSeriesKeys = Object.keys(samsungSeriesGrouped).sort((a, b) => {
    const ai = SAMSUNG_SERIES_ORDER.indexOf(a);
    const bi = SAMSUNG_SERIES_ORDER.indexOf(b);
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
  });

  const nonMacbookKeys = sortedKeys.filter((key) => grouped[key][0].brand !== 'MacBook');
  const macbookKeys = sortedKeys.filter((key) => grouped[key][0].brand === 'MacBook');

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (query.trim()) setSelectedBrand('全部');
  };

  const handleBrandClick = (b: string) => {
    setSelectedBrand(b);
    setSearchQuery('');
  };

  const handleQuickModel = (m: string) => {
    setSearchQuery(m);
    setSelectedBrand('全部');
  };

  const quickModels = ['iPhone Air', 'iPhone 16 Pro Max', 'iPhone 16', 'iPhone 15 Pro Max', 'iPhone 14 Pro Max', 'MacBook Air M3'];

  const renderModelCard = (key: string) => {
    const items = grouped[key];
    const first = items[0];
    const brandIcon = first.brand === 'iPhone' || first.brand === 'Samsung' ? 'ri-smartphone-line'
      : first.brand === 'iPad' ? 'ri-tablet-line'
      : first.brand === 'MacBook' ? 'ri-macbook-line'
      : first.brand === 'Google' ? 'ri-google-line'
      : first.brand === 'Asus' ? 'ri-gamepad-line'
      : 'ri-android-line';
    return (
      <div key={key} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <div className="w-8 h-8 flex items-center justify-center bg-orange-50 rounded-lg flex-shrink-0">
            <i className={`${brandIcon} text-orange-500`} />
          </div>
          <div>
            <span className="text-xs text-gray-400">{first.brand}</span>
            <h3 className="text-base font-bold text-gray-900">{first.model}</h3>
          </div>
        </div>
        <div className="divide-y divide-gray-50">
          {sortRepairItems(items).map((item) => (
            <div key={item.id} className="flex items-center justify-between px-6 py-4">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-gray-800">{item.repairType}</span>
                  {showUsedPartWarning(item) && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 border border-amber-200 text-amber-600 text-xs rounded-full whitespace-nowrap">
                      <i className="ri-alert-line text-xs" />維修後會提示已使用零件
                    </span>
                  )}
                  {showFaceIDOKNotice(item) && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-50 border border-green-200 text-green-600 text-xs rounded-full whitespace-nowrap">
                      <i className="ri-check-line text-xs" />維修後不會提示已使用零件
                    </span>
                  )}
                  {item.repairType === '主機板維修' && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 border border-amber-200 text-amber-600 text-xs rounded-full whitespace-nowrap">
                      <i className="ri-alert-line text-xs" />特殊情況另外報價
                    </span>
                  )}
                </div>
                <span className={`mt-1 inline-block text-xs px-2 py-0.5 rounded-full ${item.inStock ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'}`}>
                  {item.inStock ? '零件庫存中' : '需預訂零件'}
                </span>
              </div>
              <div className="text-right">
                <div className="text-xl font-bold text-orange-500">
                  {item.price === 0
                    ? '來電詢價'
                    : item.estimatedTime && /^\d+$/.test(item.estimatedTime)
                      ? `NT$${item.price.toLocaleString()} / NT$${parseInt(item.estimatedTime, 10).toLocaleString()}`
                      : `NT$${item.price.toLocaleString()}`}
                </div>
                <div className="text-xs text-gray-400">含工資</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Asus 按系列再分組
  const asusSeriesGrouped: Record<string, string[]> = {};
  sortedKeys.forEach((key) => {
    const item = grouped[key][0];
    if (item.brand !== 'Asus') return;
    const s = item.series ?? '其他';
    if (!asusSeriesGrouped[s]) asusSeriesGrouped[s] = [];
    asusSeriesGrouped[s].push(key);
  });

  const sortedAsusSeriesKeys = Object.keys(asusSeriesGrouped).sort((a, b) => {
    const ai = ASUS_SERIES_ORDER.indexOf(a);
    const bi = ASUS_SERIES_ORDER.indexOf(b);
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
  });

  const isMacbookOnly = selectedBrand === 'MacBook' && !searchQuery.trim();
  const isSamsungOnly = selectedBrand === 'Samsung' && !searchQuery.trim();
  const isAsusOnly = selectedBrand === 'Asus' && !searchQuery.trim();
  const isIphoneOnly = selectedBrand === 'iPhone' && !searchQuery.trim();
  const isIpadOnly = selectedBrand === 'iPad' && !searchQuery.trim();

  // iPad 按系列分組
  const ipadSeriesGrouped: Record<string, string[]> = {};
  sortedKeys.forEach((key) => {
    const item = grouped[key][0];
    if (item.brand !== 'iPad') return;
    const s = IPAD_SERIES_MAP[item.model] ?? 'iPad 其他';
    if (!ipadSeriesGrouped[s]) ipadSeriesGrouped[s] = [];
    ipadSeriesGrouped[s].push(key);
  });

  const sortedIpadSeriesKeys = Object.keys(ipadSeriesGrouped).sort((a, b) => {
    const ai = IPAD_SERIES_ORDER.indexOf(a);
    const bi = IPAD_SERIES_ORDER.indexOf(b);
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
  });

  // iPhone 按系列分組
  const iphoneSeriesGrouped: Record<string, string[]> = {};
  sortedKeys.forEach((key) => {
    const item = grouped[key][0];
    if (item.brand !== 'iPhone') return;
    const s = IPHONE_SERIES_MAP[item.model] ?? 'iPhone 其他';
    if (!iphoneSeriesGrouped[s]) iphoneSeriesGrouped[s] = [];
    iphoneSeriesGrouped[s].push(key);
  });

  const sortedIphoneSeriesKeys = Object.keys(iphoneSeriesGrouped).sort((a, b) => {
    const ai = IPHONE_SERIES_ORDER.indexOf(a);
    const bi = IPHONE_SERIES_ORDER.indexOf(b);
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
  });

  const renderSeriesGroup = (
    seriesGrouped: Record<string, string[]>,
    sortedSeries: string[]
  ) => (
    <div className="space-y-10">
      {sortedSeries.map((seriesName) => (
        <div key={seriesName}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 bg-orange-500 rounded-full" />
            <h2 className="text-lg font-bold text-gray-900">{seriesName}</h2>
            <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
              {seriesGrouped[seriesName].length} 個型號
            </span>
          </div>
          <div className="space-y-4">
            {seriesGrouped[seriesName].map((key) => renderModelCard(key))}
          </div>
        </div>
      ))}
    </div>
  );

  const pricingSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: '手機維修報價查詢',
    provider: {
      '@type': 'LocalBusiness',
      name: '兄弟維修',
      url: 'https://www.brotherrepair.com.tw/',
    },
    serviceType: '手機維修',
    areaServed: {
      '@type': 'City',
      name: '台北市',
    },
    description: '提供 iPhone、iPad、MacBook、Samsung、ASUS、Google Pixel 全品牌維修報價查詢，含螢幕更換、電池維修、主板維修等項目。',
    offers: [
      { '@type': 'Offer', name: 'iPhone 螢幕更換', priceCurrency: 'TWD', price: '600', priceSpecification: { '@type': 'PriceSpecification', minPrice: '600', priceCurrency: 'TWD' } },
      { '@type': 'Offer', name: 'iPhone 電池更換', priceCurrency: 'TWD', price: '600', priceSpecification: { '@type': 'PriceSpecification', minPrice: '600', priceCurrency: 'TWD' } },
      { '@type': 'Offer', name: 'MacBook 螢幕更換', priceCurrency: 'TWD', price: '6500', priceSpecification: { '@type': 'PriceSpecification', minPrice: '6500', priceCurrency: 'TWD' } },
      { '@type': 'Offer', name: '手機資料救援', priceCurrency: 'TWD', price: '1500', priceSpecification: { '@type': 'PriceSpecification', minPrice: '1500', priceCurrency: 'TWD' } },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(pricingSchema) }}
      />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="pt-32 pb-12 bg-gray-950">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <div className="inline-flex items-center gap-2 text-orange-400 text-sm font-medium mb-4">
              <span className="w-8 h-px bg-orange-400" />
              維修報價查詢
              <span className="w-8 h-px bg-orange-400" />
            </div>
            <h1 className="text-5xl font-bold text-white mb-4">維修費用查詢</h1>
            <p className="text-gray-400 mb-10">輸入您的裝置型號，即時查詢維修費用與預估時間</p>
            <SearchBox initialValue={searchQuery} onSearch={handleSearch} />
            <div className="flex flex-wrap items-center justify-center gap-2 mt-6">
              <span className="text-gray-500 text-xs">熱門型號：</span>
              {quickModels.map((m) => (
                <button key={m} onClick={() => handleQuickModel(m)}
                  className="px-3 py-1.5 bg-gray-800 hover:bg-orange-500/20 border border-gray-700 hover:border-orange-500/50 text-gray-300 hover:text-orange-400 text-xs rounded-full transition-all cursor-pointer whitespace-nowrap">
                  {m}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Notice */}
        <section className="bg-gray-900 border-t border-gray-800 py-6">
          <div className="max-w-4xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/30 rounded-2xl px-5 py-4">
              <div className="w-8 h-8 flex items-center justify-center bg-amber-500/20 rounded-lg flex-shrink-0 mt-0.5">
                <i className="ri-alert-line text-amber-400 text-base" />
              </div>
              <div>
                <p className="text-amber-400 font-semibold text-sm mb-2">風險告知</p>
                <ul className="space-y-1.5">
                  {['針對主板維修有資料遺失風險，建議自行備份。', '不開機故障，開機前無法測試到所有外配功能。', '維修後基本保固三個月。'].map((t) => (
                    <li key={t} className="flex items-start gap-2 text-xs text-gray-300 leading-relaxed">
                      <span className="mt-1 w-1 h-1 rounded-full bg-amber-400 flex-shrink-0" />{t}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="flex items-start gap-3 bg-orange-500/10 border border-orange-500/30 rounded-2xl px-5 py-4">
              <div className="w-8 h-8 flex items-center justify-center bg-orange-500/20 rounded-lg flex-shrink-0 mt-0.5">
                <i className="ri-shield-check-line text-orange-400 text-base" />
              </div>
              <div>
                <p className="text-orange-400 font-semibold text-sm mb-2">原廠零件</p>
                <ul className="space-y-1.5">
                  {['按原廠流程如需更新最新版本，會先告知客戶。', '建議維修前先進行更新至最新版本及備份。', '關閉「尋找我的 iPhone」。'].map((t) => (
                    <li key={t} className="flex items-start gap-2 text-xs text-gray-300 leading-relaxed">
                      <span className="mt-1 w-1 h-1 rounded-full bg-orange-400 flex-shrink-0" />{t}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Filters & Results */}
        <section className="py-12 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            {/* Brand Filter */}
            <div className="bg-white p-5 rounded-2xl border border-gray-100 mb-8">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm font-semibold text-gray-700 whitespace-nowrap">品牌篩選</span>
                <div className="flex items-center gap-2 flex-wrap">
                  {BRANDS.map((b) => (
                    <button key={b} onClick={() => handleBrandClick(b)}
                      className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${selectedBrand === b ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                      {b}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-20">
                <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-gray-500 text-sm">載入報價資料中...</p>
              </div>
            ) : !hasFilter ? (
              <div className="text-center py-20">
                <div className="w-20 h-20 flex items-center justify-center bg-orange-50 rounded-full mx-auto mb-5">
                  <i className="ri-search-2-line text-orange-400 text-3xl" />
                </div>
                <p className="text-gray-700 font-semibold text-lg mb-2">輸入型號或選擇品牌開始查詢</p>
                <p className="text-gray-400 text-sm mb-8">支援 iPhone、iPad、MacBook、Samsung、Asus、Google 等品牌</p>
                <div className="flex flex-wrap justify-center gap-3">
                  {BRANDS.filter((b) => b !== '全部').map((b) => (
                    <button key={b} onClick={() => handleBrandClick(b)}
                      className="px-5 py-2.5 bg-white border border-gray-200 hover:border-orange-400 hover:text-orange-500 text-gray-600 text-sm font-medium rounded-xl transition-all cursor-pointer whitespace-nowrap">
                      {b}
                    </button>
                  ))}
                </div>
              </div>
            ) : filteredData.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
                  <i className="ri-search-line text-gray-400 text-2xl" />
                </div>
                <p className="text-gray-600 font-medium mb-2">找不到相關型號</p>
                <p className="text-gray-400 text-sm">請嘗試其他關鍵字，或直接聯絡我們詢問報價</p>
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-500 mb-6">
                  找到 <span className="text-orange-500 font-semibold">{filteredData.length}</span> 項維修報價
                  {searchQuery && <span>，關鍵字：「<span className="text-gray-800">{searchQuery}</span>」</span>}
                </p>

                {/* 按品牌分組顯示 */}
                {isIphoneOnly ? (
                  renderSeriesGroup(iphoneSeriesGrouped, sortedIphoneSeriesKeys)
                ) : isIpadOnly ? (
                  renderSeriesGroup(ipadSeriesGrouped, sortedIpadSeriesKeys)
                ) : isMacbookOnly ? (
                  renderSeriesGroup(macbookSeriesGrouped, sortedSeriesKeys)
                ) : isSamsungOnly ? (
                  renderSeriesGroup(
                    (() => {
                      const sg: Record<string, string[]> = {};
                      sortedKeys.filter((k) => grouped[k][0].brand === 'Samsung').forEach((k) => {
                        const s = grouped[k][0].series ?? '其他';
                        if (!sg[s]) sg[s] = [];
                        sg[s].push(k);
                      });
                      return sg;
                    })(),
                    SAMSUNG_SERIES_ORDER.filter((s) => {
                      const sg: Record<string, boolean> = {};
                      sortedKeys.filter((k) => grouped[k][0].brand === 'Samsung').forEach((k) => { sg[grouped[k][0].series ?? '其他'] = true; });
                      return sg[s];
                    })
                  )
                ) : isAsusOnly ? (
                  renderSeriesGroup(
                    (() => {
                      const ag: Record<string, string[]> = {};
                      sortedKeys.filter((k) => grouped[k][0].brand === 'Asus').forEach((k) => {
                        const s = grouped[k][0].series ?? '其他';
                        if (!ag[s]) ag[s] = [];
                        ag[s].push(k);
                      });
                      return ag;
                    })(),
                    ASUS_SERIES_ORDER.filter((s) => {
                      const ag: Record<string, boolean> = {};
                      sortedKeys.filter((k) => grouped[k][0].brand === 'Asus').forEach((k) => { ag[grouped[k][0].series ?? '其他'] = true; });
                      return ag[s];
                    })
                  )
                ) : (
                  <div className="space-y-6">
                    {nonMacbookKeys.filter((k) => grouped[k][0].brand !== 'Samsung' && grouped[k][0].brand !== 'Asus').map((key) => renderModelCard(key))}
                    {/* Samsung 搜尋結果按系列分組 */}
                    {sortedKeys.filter((k) => grouped[k][0].brand === 'Samsung').length > 0 && (
                      renderSeriesGroup(
                        (() => {
                          const sg: Record<string, string[]> = {};
                          sortedKeys.filter((k) => grouped[k][0].brand === 'Samsung').forEach((k) => {
                            const s = grouped[k][0].series ?? '其他';
                            if (!sg[s]) sg[s] = [];
                            sg[s].push(k);
                          });
                          return sg;
                        })(),
                        SAMSUNG_SERIES_ORDER.filter((s) => {
                          const sg: Record<string, boolean> = {};
                          sortedKeys.filter((k) => grouped[k][0].brand === 'Samsung').forEach((k) => { sg[grouped[k][0].series ?? '其他'] = true; });
                          return sg[s];
                        })
                      )
                    )}
                    {/* Asus 搜尋結果按系列分組 */}
                    {sortedKeys.filter((k) => grouped[k][0].brand === 'Asus').length > 0 && (
                      renderSeriesGroup(
                        (() => {
                          const ag: Record<string, string[]> = {};
                          sortedKeys.filter((k) => grouped[k][0].brand === 'Asus').forEach((k) => {
                            const s = grouped[k][0].series ?? '其他';
                            if (!ag[s]) ag[s] = [];
                            ag[s].push(k);
                          });
                          return ag;
                        })(),
                        ASUS_SERIES_ORDER.filter((s) => {
                          const ag: Record<string, boolean> = {};
                          sortedKeys.filter((k) => grouped[k][0].brand === 'Asus').forEach((k) => { ag[grouped[k][0].series ?? '其他'] = true; });
                          return ag[s];
                        })
                      )
                    )}
                    {/* MacBook 搜尋結果按系列分組 */}
                    {macbookKeys.length > 0 && renderSeriesGroup(
                      (() => {
                        const mg: Record<string, string[]> = {};
                        macbookKeys.forEach((k) => {
                          const s = grouped[k][0].series ?? '其他';
                          if (!mg[s]) mg[s] = [];
                          mg[s].push(k);
                        });
                        return mg;
                      })(),
                      sortedSeriesKeys
                    )}
                  </div>
                )}
              </>
            )}

            <div className="mt-8 bg-orange-50 border border-orange-100 rounded-2xl p-5 flex items-start gap-3">
              <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                <i className="ri-information-line text-orange-500" />
              </div>
              <p className="text-sm text-orange-700 leading-relaxed">
                以上報價僅供參考，實際費用依裝置狀況而定。維修前我們會提供精確報價，確認後再施工。免費到店診斷，歡迎帶機詢問！
              </p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
